// Reliable primary images from design guidelines
const IMG = (id, w = 1200) =>
  `https://images.unsplash.com/photo-${id}?crop=entropy&cs=srgb&fm=jpg&w=${w}&q=80`;

export const HERO_IMG =
  "https://images.unsplash.com/photo-1685873875872-ecd77ba5a2b0?crop=entropy&cs=srgb&fm=jpg&w=1600&q=80";

// Fallback used if any image fails to load
export const FALLBACK_IMG = IMG("1758922584983-82ffd5720c6a");

export const IMAGES = {
  studio: IMG("1758922584983-82ffd5720c6a"),
  family: IMG("1685873875872-ecd77ba5a2b0"),
  maternity: IMG("1543342384-1f1350e27861"),
  couples: "/couples-engagements.webp",
  portraitA: IMG("1494790108377-be9c29b29330"),
  portraitB: IMG("1531123897727-8f129e1688ce"),
  portraitC: IMG("1524504388940-b1c1722653e1"),
  headshot: IMG("1500648767791-00dcc994a43e"),
  woman: IMG("1438761681033-6461ffad8d80"),
  man: IMG("1502823403499-6ccfcf4fb453"),
  lifestyle: IMG("1517841905240-472988babdf9"),
  maternity2: IMG("1522075469751-3a6694fb2f61"),
};

export const NAV_LINKS = [
  { label: "Home", to: "/" },
  { label: "Meet Jac", to: "/meet-jac" },
  { label: "Portfolio", to: "/portfolio" },
  { label: "Investment", to: "/investment" },
  { label: "Locations", to: "/locations" },
  { label: "Studio Rental", to: "/studio-rental" },
  { label: "FAQ", to: "/faq" },
  { label: "Contact", to: "/contact" },
];

export const BOOKING_URL = "https://book.usesession.com/i/zvTVBjRIv";
export const STUDIO_BOOKING_URL = "https://book.usesession.com/t/e_qAYgTFgu";

// Each session type links to its own external booking page (replace "#" with your links)
export const SESSIONS = [
  {
    key: "maternity",
    name: "Maternity",
    blurb: "Soft, reverent frames of the in-between.",
    description:
      "This chapter is worth honoring. Maternity sessions with me feel unhurried and reverent — soft light, flowy fabrics, and gentle direction to help you feel like yourself. Whether we shoot in the studio or an outdoor field at golden hour, the goal is the same: quiet, timeless imagery of you and your growing family.",
    img: "/maternity-hero.jpg",
    includes: [
      "Up to 30 minutes",
      "1 location",
      "40+ images for viewing",
      "20 digital downloads",
      "Option to buy additional downloads",
      "Option to buy full gallery",
    ],
    tiers: [{ name: "Maternity Session", price: "$375" }],
    bookUrl: "https://book.usesession.com/t/DrIL6Ncrjt",
  },
  {
    key: "family",
    name: "Family",
    blurb: "Unhurried, honest frames of your people.",
    description:
      "Family sessions are the heart of what I do. Whether it's your little ones' first outdoor session, the yearly gathering, or a big-family reunion, I promise a steady, no-fuss experience. I bring styling guidance beforehand and lots of gentle prompts on the day so you get real laughter, real snuggles, and images that actually look like your family — not a stock catalog.",
    img: "/family-6.jpg",
    includes: ["Private online gallery", "Print release", "Styling guidance"],
    tiers: [
      {
        name: "The Mini Session",
        price: "$350",
        retainer: "$100 retainer to book (applied to total)",
        desc: "A quick, sweet session for the essentials.",
        features: [
          "Up to 30 minutes",
          "1 location",
          "30+ images for viewing",
          "15 digital downloads",
          "Option to buy additional downloads",
          "Option to buy full gallery",
        ],
      },
      {
        name: "The Full Session",
        price: "$450",
        popular: true,
        retainer: "$100 retainer to book (applied to total)",
        desc: "Our most-loved family experience.",
        features: [
          "Up to 1 hour",
          "1 location",
          "50+ images for viewing",
          "30 digital downloads",
          "Option to buy additional downloads",
          "Option to buy full gallery",
        ],
      },
      {
        name: "The Extended Family Session",
        priceFrom: "$550",
        bookUrl: "https://book.usesession.com/t/kBvx9nkO3B",
        desc: "For larger gatherings — 3 packages sized to your group.",
        features: ["11–20, 20–30, or unlimited members", "40 – unlimited downloads"],
        subPackages: [
          {
            name: "11–20 Family Members",
            price: "$550",
            retainer: "$200 retainer to book (applied to total)",
            desc: "For a larger gathering, styled and steady.",
            features: ["11–20 family members", "40 digital downloads"],
          },
          {
            name: "20–30 Family Members",
            price: "$650",
            retainer: "$200 retainer to book (applied to total)",
            desc: "For a big-family reunion vibe.",
            features: ["20–30 family members", "50 digital downloads"],
          },
          {
            name: "Unlimited Package",
            price: "$850",
            retainer: "$200 retainer to book (applied to total)",
            desc: "Everyone. Every combination. The full gallery.",
            features: ["Unlimited family members", "Unlimited gallery"],
          },
        ],
      },
    ],
    bookUrl: "https://book.usesession.com/t/Khl6XKoGBs",
  },
  {
    key: "seniors",
    name: "Seniors",
    blurb: "A confident chapter marker before everything changes.",
    description:
      "Senior year deserves more than a rushed hallway backdrop. My senior sessions are relaxed, collaborative, and built around who you are — the sports you love, the outfits you feel best in, the corner of Acadiana that means the most. I direct enough to make you comfortable in front of the lens but leave plenty of room for your real personality to come through.",
    img: "/senior-1.webp",
    includes: ["Private online gallery", "Print release", "Outfit changes"],
    tiers: [
      {
        name: "The Essential Senior Collection",
        price: "$400",
        retainer: "$150 retainer to book (applied to total)",
        desc: "Perfect for the senior who only wants \u201Ca few good shots.\u201D",
        features: [
          "Up to 30 minutes of shoot time",
          "1 location in Acadiana area",
          "1 outfit",
          "Online gallery with 40+ high-resolution images",
          "20 high-resolution edited images to select and download",
          "Additional images available for purchase",
        ],
      },
      {
        name: "The Timeless Senior Collection",
        price: "$500",
        popular: true,
        retainer: "$150 retainer to book (applied to total)",
        desc: "Perfect for any high school or college graduate.",
        features: [
          "Up to 60 minutes of shoot time",
          "Up to 3 outfit choices",
          "1 location in Acadiana area",
          "Online gallery with 60+ high-resolution images",
          "40 high-resolution edited images to select and download",
          "Additional images available for purchase",
        ],
      },
      {
        name: "The Legacy Senior Collection",
        price: "$625",
        retainer: "$150 retainer to book (applied to total)",
        desc: "Perfect for the ones who want to stand out and show off their successes.",
        features: [
          "Up to 2 hours of shoot time",
          "Up to 5 outfit choices",
          "Acadiana area +",
          "Online gallery with 90+ high-resolution images",
          "60 high-resolution edited images to select and download",
          "Additional images available for purchase",
        ],
      },
      {
        name: "The Senior Circle Collection",
        price: "$700",
        retainer: "$150 retainer to book (applied to total)",
        desc: "Celebrate senior year with your best friend \u2014 a shared session designed for two.",
        features: [
          "Up to 2 hours of shoot time",
          "Up to 3 outfit choices per senior",
          "1\u20132 locations in the Acadiana area",
          "Online gallery with 90+ high-resolution images",
          "30 high-resolution edited images to select and download per senior",
          "Additional images available for purchase",
        ],
      },
    ],
    bookUrl: "https://book.usesession.com/t/qUc41BXESl",
  },
  {
    key: "branding",
    name: "Headshots & Branding",
    blurb: "Imagery that lets your work be taken seriously.",
    description:
      "For entrepreneurs, creatives, and small teams who need imagery that matches how good their work actually is. From a quick 15-minute LinkedIn refresh to a full two-hour on-location brand story, every session is planned with your website, socials, and marketing in mind — so you leave with a library, not just a headshot.",
    img: "/branding-1.jpg",
    includes: ["Studio or on-location", "Retouched images", "Commercial use"],
    tiers: [
      {
        name: "Quick Headshot Refresh",
        price: "$175",
        retainer: "$50 retainer to book (applied to total)",
        desc: "In-studio, streamlined for busy professionals refreshing LinkedIn, websites, or business cards.",
        features: [
          "Up to 15 minutes of shoot time in-studio",
          "Custom color backdrop (white, black, or tan)",
          "Quick top or jacket change allowed",
          "Online gallery with 20+ high-resolution images",
          "5 digital downloads",
          "Option to purchase additional images or full gallery",
        ],
      },
      {
        name: "Lifestyle Headshots",
        price: "$350",
        retainer: "$100 retainer to book (applied to total)",
        desc: "Casual, authentic portraits in natural settings \u2014 ideal for creatives, coaches, and personal brands.",
        features: [
          "Up to 30 minutes of shoot time",
          "Selected Acadiana-area location (typically home or office)",
          "Up to 2 outfit changes",
          "Online gallery with 30+ high-resolution images",
          "15 digital downloads",
          "Add-on half hour available for $100",
        ],
      },
      {
        name: "Branding in Studio",
        price: "$450",
        retainer: "$100 retainer to book (applied to total)",
        desc: "A controlled environment for versatile brand assets \u2014 product shoots, consistent team portraits.",
        features: [
          "Up to 60 minutes of shoot time in-studio",
          "Up to 4 outfit changes",
          "Custom color backdrop (white, black, or tan)",
          "Bring props relevant to your industry",
          "Online gallery with 50+ high-resolution images",
          "30 edited digital downloads",
          "Add-on half hour available for $100",
        ],
      },
      {
        name: "The Signature (On Location)",
        price: "$550",
        popular: true,
        retainer: "$150 retainer to book (applied to total)",
        desc: "A mix of headshots and brand storytelling shot at your business.",
        features: [
          "Up to 1-hour session at your business",
          "Individual headshots + team/group shots",
          "Workspace, product, or behind-the-scenes lifestyle images",
          "40 fully edited digital downloads",
          "Online gallery",
          "Option to buy additional images or full gallery",
        ],
      },
      {
        name: "The Full Brand Story (On Location)",
        price: "$750",
        retainer: "$150 retainer to book (applied to total)",
        desc: "Build a strong content library for social media, website, and marketing.",
        features: [
          "2-hour session at your business",
          "Individual headshots + multiple team combinations",
          "Candid lifestyle, workspace details, product/service photos",
          "80 fully edited digital downloads",
          "Online gallery",
          "Option to buy additional images or full gallery",
        ],
      },
    ],
    bookUrl: "https://book.usesession.com/t/aB9xz1AQS3",
  },
  {
    key: "events",
    name: "Events",
    blurb: "Capturing the energy, moments, and memories of your special event.",
    description:
      "Showers, corporate events, anniversaries, parties of all kinds, banquets, and benefits — event coverage is designed to blend into the day and quietly capture the moments that matter.",
    img: "/event-1.jpg",
    includes: ["Unlimited edited images", "Online gallery", "2-hour minimum"],
    tiers: [
      {
        name: "Collection 1",
        price: "$500",
        retainer: "$100 retainer to book (applied to total)",
        desc: "For shorter celebrations \u2014 showers, brunches, small parties.",
        features: ["2 hours of coverage", "Unlimited edited images", "Online gallery to share"],
      },
      {
        name: "Collection 2",
        price: "$750",
        popular: true,
        retainer: "$100 retainer to book (applied to total)",
        desc: "The most-booked event coverage.",
        features: ["3 hours of coverage", "Unlimited edited images", "Online gallery to share"],
      },
      {
        name: "Collection 3",
        price: "$1,000",
        retainer: "$100 retainer to book (applied to total)",
        desc: "For full-length events, ceremonies, and receptions.",
        features: ["4 hours of coverage", "Unlimited edited images", "Online gallery to share"],
      },
    ],
    bookUrl: "https://book.usesession.com/t/Vl0E_zMjVL",
  },
  {
    key: "fresh48",
    name: "Fresh 48",
    blurb: "The first tender hours, exactly as they were.",
    description:
      "Fresh 48 sessions happen in the first 24–48 hours after your baby is born — right there in the hospital room. I document quietly: the tiny fingers, the first family snuggle, siblings meeting, all the little details you'll want to remember. No posing, no fuss — just the softest, most sacred morning of your life.",
    img: "/fresh48-nash.jpg",
    includes: ["Up to 1 hour at the hospital", "Private online gallery", "30 digital downloads"],
    tiers: [
      {
        name: "Fresh 48 Session",
        price: "$450",
        retainer: "Louisiana sales tax added at booking",
        desc: "Quiet, unposed hospital coverage of your first hours together.",
        features: [
          "Up to 1 hour of coverage at the hospital",
          "50+ edited images in an online gallery for viewing",
          "30 digital downloads",
          "Option to purchase additional downloads or the full gallery",
          "Add-on: 24-hour turnaround for announcement images (additional cost)",
        ],
      },
    ],
    bookUrl: "https://book.usesession.com/t/VOJ4HCxU8X",
  },
  {
    key: "couples",
    name: "Couples & Engagements",
    blurb: "The quiet gravity of two people, held still.",
    description:
      "Whether you just got engaged, want to mark your first anniversary, or simply want beautiful pictures of the two of you, couples sessions feel like a slow walk together with a camera along. I prompt you into real interactions — a whispered joke, a slow dance in the field — so the images feel like you, not a pose.",
    img: IMAGES.couples,
    includes: ["Private online gallery", "Print release", "Styling guidance"],
    tiers: [
      {
        name: "The Mini Session",
        price: "$350",
        retainer: "$100 retainer to book (applied to total)",
        desc: "A quick, sweet session for the essentials.",
        features: [
          "Up to 30 minutes",
          "1 location",
          "30+ images for viewing",
          "15 digital downloads",
          "Option to buy additional downloads",
          "Option to buy full gallery",
        ],
      },
      {
        name: "The Full Session",
        price: "$450",
        popular: true,
        retainer: "$100 retainer to book (applied to total)",
        desc: "Our most-loved couples experience.",
        features: [
          "Up to 1 hour",
          "1 location",
          "50+ images for viewing",
          "30 digital downloads",
          "Option to buy additional downloads",
          "Option to buy full gallery",
        ],
      },
    ],
    bookUrl: "https://book.usesession.com/t/UFuXYDjKtU",
  },
  {
    key: "newborn",
    name: "Lifestyle Newborn",
    blurb: "First days at home, exactly as they were.",
    description:
      "Lifestyle newborn sessions happen in the comfort of your own home in the first two weeks. Baby stays in whatever they'd naturally be wearing, family stays cozy in their real spaces, and I quietly document the tender in-between: feeding, nuzzles, sleepy stretches, siblings peeking in. Nothing forced, everything remembered.",
    img: "/newborn-lifestyle.jpg",
    includes: ["Private online gallery", "Print release", "In-home experience"],
    finePrint:
      "Best scheduled within the first two weeks of baby's arrival — reach out during your third trimester so I can hold a tentative date and adjust once your little one is here.",
    tiers: [
      {
        name: "The Welcome Session",
        price: "$450",
        retainer: "$100 retainer to book (applied to total)",
        desc: "Sweet, essential-only frames for your first days at home.",
        features: [
          "Up to 60 minutes, in-home",
          "Baby + parents",
          "Simple posing, real moments",
          "50+ images for viewing",
          "30 digital downloads",
          "Option to buy additional downloads",
        ],
      },
      {
        name: "The Homecoming Session",
        price: "$550",
        popular: true,
        retainer: "$100 retainer to book (applied to total)",
        desc: "The complete story — family, siblings, and quiet nursery details.",
        features: [
          "Up to 90 minutes, in-home",
          "Baby, parents & siblings",
          "Family portraits included",
          "Nursery detail shots",
          "70+ images for viewing",
          "45 digital downloads",
          "Option to buy additional downloads",
          "Option to buy full gallery",
        ],
      },
    ],
    bookUrl: "https://book.usesession.com/t/A_ZdDdE41F",
  },
  {
    key: "cakesmash",
    name: "Milestone Portraits",
    blurb: "For every big moment worth remembering.",
    description:
      "From first birthdays and cake smashes to preschool graduations, lost-tooth grins, big-kid birthdays, and those in-between preteen years — milestone sessions are for celebrating the ages and stages that go by way too fast. We'll style the session around what your child is celebrating, keep it playful, and give you images that mark exactly who they are right now.",
    img: "/milestone-session.jpg",
    includes: [
      "Up to 1 hour",
      "1 location",
      "50+ images for viewing",
      "30 digital downloads",
      "Option to buy additional downloads",
      "Option to buy full gallery",
    ],
    tiers: [{ name: "Milestone Session", price: "$400" }],
    finePrint:
      "Milestone sessions are typically done in-studio, but they can also be done outdoors during sunset — just let me know your preference when you book.",
    bookUrl: "https://book.usesession.com/t/u9FqxRzjTC",
  },
];

// All-inclusive baby packages shown in the Bundles pop-up
export const BUNDLES = [
  {
    name: "The Motherhood Story",
    price: "$750",
    blurb: "The Bump to Baby Bundle · Maternity • The Welcome Session",
    bookUrl: "https://book.usesession.com/t/u9ZEXfXMIT",
    description:
      "Celebrate one of life's most meaningful journeys with two sessions designed to preserve every stage of welcoming your little one. From the reverent quiet of your maternity session to the sweet, intimate first-days-at-home of a lifestyle newborn session, this bundle tells the story of your growing family — and saves $100 booked together versus booking them separately.",
    includes: [
      "Maternity — up to 30 min, 1 location (20 downloads)",
      "The Welcome Session — up to 60 min, in-home with baby + parents (30 downloads)",
      "Online gallery for each session for viewing",
      "Option to buy additional downloads or full gallery",
      "$300 retainer to book (applied to total)",
    ],
  },
  {
    name: "First Year Collective",
    price: "$1,425",
    blurb: "Maternity • Welcome Session • 6 Months • One Year",
    bookUrl: "https://book.usesession.com/t/IAdtJBOefK",
    description:
      "Your baby's first year is filled with moments you'll never get back. The First Year Collective is designed to preserve every chapter, from the excitement of pregnancy to your little one's first birthday. Four thoughtfully planned sessions, one seamless experience — and $250 saved versus booking each session on its own.",
    includes: [
      "Maternity — up to 30 min, 1 location (20 downloads)",
      "The Welcome Session — up to 60 min, in-home with baby + parents (30 downloads)",
      "6-Month Milestone — up to 1 hr (30 downloads)",
      "One-Year Session — up to 1 hr (30 downloads)",
      "Online gallery for each session for viewing",
      "Option to buy additional downloads or full galleries",
      "$450 retainer to book (applied to total)",
    ],
  },
];

export const CATEGORIES = [
  {
    key: "families",
    title: "Families",
    img: "/family-1.jpg",
    blurb: "Unhurried, honest frames of the people you love most.",
  },
  {
    key: "seniors",
    title: "Seniors",
    img: "/senior-1.webp",
    blurb: "A confident chapter marker before everything changes.",
  },
  {
    key: "branding",
    title: "Branding & Headshots",
    img: "/branding-home.webp",
    blurb: "Imagery that lets your work be taken seriously.",
  },
  {
    key: "maternity",
    title: "Maternity",
    img: "/maternity-hero.jpg",
    blurb: "Soft, reverent frames of the in-between.",
  },
  {
    key: "couples",
    title: "Couples",
    img: IMAGES.couples,
    blurb: "The quiet gravity of two people, held still.",
  },
  {
    key: "newborn",
    title: "Lifestyle Newborn",
    img: "/newborn-lifestyle.jpg",
    blurb: "First days at home, exactly as they were.",
  },
  {
    key: "fresh48",
    title: "Fresh 48",
    img: "/fresh48-1.jpg",
    blurb: "The first hours — nose to nose, in the hospital where it all began.",
  },
  {
    key: "boudoir",
    title: "Boudoir",
    img: IMAGES.portraitA,
    blurb: "Tender, empowering, and entirely yours.",
    hidden: true,
  },
  {
    key: "heirloom",
    title: "Heirloom",
    img: "/heirloom-1.jpg",
    blurb: "Timeless, painterly portraits made to be framed and passed down.",
  },
  {
    key: "milestone",
    title: "First Year & Cake Smash — Milestone",
    img: "/milestone-session.jpg",
    blurb: "First birthdays, cake smashes, and the little chapters in between.",
  },
];

export const MANIFESTO = [
  {
    no: "01",
    title: "Light before everything",
    body: "Every session begins with the way light falls. I chase the soft edge of a window, the last hour of sun, the glow that makes skin look like memory.",
    img: IMAGES.woman,
  },
  {
    no: "02",
    title: "Presence over posing",
    body: "The best frames arrive when you forget the camera. I direct gently, then step back and wait for the real thing to surface.",
    img: IMAGES.family,
  },
  {
    no: "03",
    title: "Made to be printed",
    body: "These images are not meant to disappear into a feed. They are made to live on walls, in albums, and in hands for a very long time.",
    img: IMAGES.studio,
  },
];

export const TESTIMONIALS = [
  {
    quote:
      "She was amazing. Patient. Up for challenges and made us laugh too. And in an hour she made magic happen. Definitely using her again.",
    name: "A. Wiltz",
  },
  {
    quote:
      "Highly recommend. Jacqueline was super nice and laid back. The photos were quickly and beautifully edited.",
    name: "E. Guidry",
  },
  {
    quote:
      "We had a great extended family session with Jacqueline! She's personable and easy to work with! I liked her posing and editing. Can't wait to have another shoot!",
    name: "B. Bonin",
  },
  {
    quote:
      "Jacqueline took my daughter's senior photos. She suggested locations and poses for each outfit. Jacqueline communicated with us from scheduling the session to editing and downloading the photos. We are extremely pleased with her services and pricing. Hope to schedule a family session with her soon!",
    name: "R. Breaux",
  },
];

export const PACKAGES = [
  {
    name: "The Portrait",
    price: "$450",
    duration: "45 minutes",
    features: [
      "Single location",
      "One outfit change",
      "20 edited digital images",
      "Private online gallery",
    ],
  },
  {
    name: "The Signature",
    price: "$850",
    duration: "90 minutes",
    features: [
      "Two locations",
      "Unlimited outfit changes",
      "50 edited digital images",
      "Print release + styling guide",
    ],
    featured: true,
  },
  {
    name: "The Story",
    price: "$1,450",
    duration: "Half day",
    features: [
      "Multiple locations",
      "Full lifestyle coverage",
      "120+ edited digital images",
      "Heirloom album included",
    ],
  },
];

export const FAQS = [
  {
    q: "I am ready to book, now what?",
    a: "First, take a moment to review the session details and pricing on the Investment page. Once you've found the session that fits, you can book directly through the Investment tab or submit the contact form if you'd like to chat through the details first. To secure your date, a retainer and signed contract will be required at the time of booking. The remaining balance for the session will be due on the day of the session.",
  },
  {
    q: "How long is the session?",
    a: "Every session length is unique to the package that you choose upon booking.",
  },
  {
    q: "Can we bring our pet?",
    a: "I want to say absolutely, but it truly depends on the location and any rules that may apply. I love all the fur babies! However, if your pet is aggressive I ask you keep him or her home. It will be your responsibility for keeping your pet leashed or caged and ensure he/she does not run off.",
  },
  {
    q: "What do we bring?",
    a: "If you have children in the session, consider bringing a treat or a special toy to help capture their attention and reward them. Please feel free to bring water and a pair of shoes to change into afterward, especially if we will be doing some walking. Additionally, if we discussed outfit changes or specific props, please remember to bring those along.",
  },
  {
    q: "Do you travel?",
    a: "Yes, I am based out of Lafayette but will travel. If the location is more than 30 miles from my home, then a travel fee may apply.",
  },
  {
    q: "How & when do we get our photos? Can additional images be purchased?",
    a: "Turnaround is 2–4 weeks. In less busy seasons, I try to deliver in 2–3 weeks; during busier seasons, please expect up to 4 weeks. I'll send you a link via email (unless we've agreed on another method of communication) to view, download, and purchase additional images. Additional images are $15 each, or you can purchase the entire gallery for $150.",
  },
  {
    q: "What if the weather is an issue?",
    a: "If inclement weather, such as rain or other unfavorable conditions, prevents us from proceeding with your session, we will promptly discuss rescheduling options. Given Louisiana's unpredictable weather, I will make informed decisions on the day of the session. Open communication is a priority, and I will keep you updated to ensure we address any weather-related concerns effectively.",
  },
  {
    q: "What happens if we become ill?",
    a: "If you're feeling unwell, please don't hesitate to reach out and let me know. I completely understand that life can be unpredictable, and we can work together to reschedule your session. However, please keep in mind that you can only reschedule up to three times. After the third reschedule, the retainer will be forfeited. Thank you for your understanding!",
  },
];

// —————————————————————————————————————————————
// Editing Queue — status board for current turnaround
// Update these when your workflow changes.
// —————————————————————————————————————————————
export const EDITING_QUEUE = {
  updatedOn: "October 28, 2026",
  currentTurnaround: "2–4 weeks",
  status: "On Schedule",
  intro:
    "A quiet peek behind the curtain. This is where every session lives between the shutter and the final gallery — and roughly when you can expect yours to land in your inbox.",
  batches: [
    {
      label: "Currently Editing",
      window: "Andrea Breaux",
    },
    {
      label: "Up Next",
      window: "Kori Sarver",
      note: "Full galleries delivering within 2–3 weeks.",
    },
    {
      label: "In the Queue",
      window: "Holiday Portraits With Santa",
      note: "Culling begins as soon as your session wraps.",
    },
  ],
  process: [
    {
      step: "01",
      title: "Cull & Cure",
      body: "Within 48 hours of your session, I back up every frame twice and start the slow work of choosing the very best.",
    },
    {
      step: "02",
      title: "Full Edit",
      body: "Every image is color-balanced, retouched, and finished by hand — no presets and pray, no AI shortcuts.",
    },
    {
      step: "03",
      title: "Gallery Delivery",
      body: "A private online gallery lands in your inbox within 2–4 weeks, ready to download, share, and print.",
    },
  ],
  footnote:
    "Busy season (October — December) may push turnaround closer to the 4-week mark. If anything changes, I'll email you personally.",
};

// —————————————————————————————————————————————
// Client Guide — What to wear + how to prepare
// —————————————————————————————————————————————
export const CLIENT_GUIDE = {
  intro:
    "The right outfit disappears the moment you step in front of the camera — it lets the people, the light, and the place speak for themselves. Here's what I've learned works, session after session.",
  principles: [
    {
      title: "Lean into neutrals",
      body: "Cream, oat, sage, sand, dusty blue, terracotta, olive. Muted tones photograph like poetry. Save bright reds and neon for date night — they cast onto skin and pull the eye away from you.",
    },
    {
      title: "Layers & texture win",
      body: "Linen, gauze, knit, denim, corduroy, flowy fabric. Texture reads gorgeous on camera and adds quiet dimension. Wrinkles are welcome — steaming right before helps, but don't stress.",
    },
    {
      title: "Coordinate — don't match",
      body: "Choose two or three colors from the same palette and let each person wear a different piece of it. Avoid head-to-toe matchy sets or full family khaki-and-white uniforms.",
    },
    {
      title: "Movement matters",
      body: "Flowy dresses, midi skirts, and untucked shirts move beautifully. If you love it and want it captured in motion — bring it.",
    },
    {
      title: "Skip busy patterns & logos",
      body: "Big graphic prints, thin stripes, and brand logos date quickly and distract from faces. If you love a pattern, keep it small and let one person wear it while others stay solid.",
    },
    {
      title: "Feet count",
      body: "Choose shoes you'd actually wear in the photo — bare feet, boots, sandals, sneakers all photograph beautifully. Just make sure they match the vibe of the outfit.",
    },
  ],
  bySession: [
    {
      title: "Families",
      tips: [
        "Pick a color palette of 3 — earth tones, coastal, or garden — and dress each person from within it.",
        "Layer textures: mom in a flowy dress, dad in linen or chambray, kids in knit or gauze.",
        "Bring a small treat or favorite toy for the little ones. It buys us five golden minutes.",
      ],
    },
    {
      title: "Maternity",
      tips: [
        "Long, flowy dresses in cream, blush, sage, or deep jewel tones photograph beautifully — especially in fields and golden light.",
        "Fitted knits show off the bump; empire waistlines flatter every stage.",
        "Bring a second look if you want one soft/one dramatic frame — I'll help you plan the swap.",
      ],
    },
    {
      title: "Newborn & Fresh 48",
      tips: [
        "For hospital Fresh 48s: bring one soft neutral swaddle and the outfit you're bringing baby home in. That's it — the room is the story.",
        "For in-home lifestyle: parents in neutrals (cream, oatmeal, chambray), baby in a simple onesie or wrap.",
        "Skip anything with big bows, headbands, or logos — timeless is the goal.",
      ],
    },
    {
      title: "Seniors",
      tips: [
        "Bring 2–3 outfits: one you feel confident in, one that shows your personality, one your parents want (trust me).",
        "Textures over patterns. Denim, leather, knit, silk — all photograph incredibly.",
        "Do your makeup a shade heavier than everyday — cameras eat a little of it.",
      ],
    },
    {
      title: "Couples & Engagements",
      tips: [
        "Coordinate palettes, not outfits. She in a flowy dress + him in a button-down and slacks or dark denim is a foolproof pairing.",
        "Consider two looks if we have time — one dressy, one relaxed — for range.",
        "Bring anything meaningful: your dog, a bottle of wine, the record you danced to. It grounds the photos.",
      ],
    },
    {
      title: "Branding & Headshots",
      tips: [
        "Lead with your brand's palette. If your website is warm and earthy, dress like your website.",
        "Bring 2–3 tops and one \"wildcard\" — a blazer, a signature necklace, a beloved sweater.",
        "Have your props ready: laptop, notebook, product samples, coffee. We'll build scenes around them.",
      ],
    },
  ],
  dayOf: [
    "Hydrate the day before (skin loves it).",
    "Arrive with clothes steamed and hung — not folded in a bag.",
    "Eat something light before we shoot. Hangry is real.",
    "Bring a small hair brush, chapstick, and any lipstick you want to touch up.",
    "Leave the wristwatch tan lines and hair ties at home — or at least off your wrist during the shoot.",
    "Most of all: come rested and open. The camera loves ease.",
  ],
  styleBoard: [
    { src: "/family-7.webp", caption: "Coordinated earth-tones, mixed textures", cat: "Families", catKey: "families" },
    { src: "/maternity-1.jpg", caption: "Cream + soft neutrals, flowing silhouette", cat: "Maternity", catKey: "maternity" },
    { src: "/couples-2.webp", caption: "Dusty blue + denim, effortless pairing", cat: "Couples", catKey: "couples" },
    { src: "/senior-3.webp", caption: "One statement piece, everything else quiet", cat: "Seniors", catKey: "seniors" },
    { src: "/family-2.jpg", caption: "Palette of three, no full matching", cat: "Families", catKey: "families" },
    { src: "/maternity-6.jpg", caption: "Long lines, warm neutrals, golden hour", cat: "Maternity", catKey: "maternity" },
    { src: "/newborn-lifestyle.jpg", caption: "Parents in oatmeal, baby in cream", cat: "Newborn", catKey: "newborn" },
    { src: "/branding-2.webp", caption: "Textured knit, on-brand palette", cat: "Branding", catKey: "branding" },
  ],
};

export const REVEAL_REEL = {
  eyebrow: "Latest sessions",
  images: [
    "/wavy-21.jpg",
    "/wavy-136.jpg",
    "/family-studio-1.webp",
    "/lavergne-41.webp",
    "/hanks-50.jpg",
    "/leblanc-33.jpg",
    "/maternity-1.jpg",
    "/couples-2.webp",
    "/senior-3.webp",
    "/newborn-lifestyle.jpg",
    "/senior-5.webp",
  ],
};

export const BOOKING_STATUS = {
  visible: true, // set to false to hide the badge on Home without deleting anything
  label: "Now Booking",
  detail: "Fall 2026 sessions",
  to: "/investment",
};

export const VIP_LIST = {
  eyebrow: "The VIP list",
  title: "A quieter corner of the studio",
  body: "Once a month, I send a small, thoughtful note straight to your inbox — first look at seasonal mini-sessions, early booking access before dates go public, styling notes for the upcoming season, and the occasional subscriber-only offer that never touches social media.",
  ctaLabel: "Join the list",
  ctaHref: "https://jacquelineclairephoto.myflodesk.com/joinemaillist",
};

// JOURNAL — session recaps, styling tips, and personal notes.
// Each post: { slug, title, date, category, hero, excerpt, story: [paragraphs], gallery: [srcs] }
// slug becomes the URL: /journal/{slug}. Keep it lowercase, hyphenated.
export const JOURNAL = {
  eyebrow: "The Journal",
  headline: "Stories from behind the lens",
  blurb:
    "Session recaps, styling notes, and the occasional personal chapter — a slower place to linger over the work.",
  emptyTitle: "The first entry is on its way",
  emptyBody:
    "I'm putting the finishing touches on the first story now — session recaps, styling notes, and a few personal chapters. Check back soon.",
  posts: [
    {
      slug: "why-location-matters-more-than-you-think",
      title: "Why Location Matters More Than You Think",
      date: "August 2026",
      category: "Sessions",
      hero: "/boudreaux-3.jpg",
      excerpt:
        "Your location is more than a pretty background — it sets the mood, tells a piece of your story, and can completely change the way your images feel.",
      story: [
        "When you think about family photos, you might think the most important part is finding the perfect outfits, getting everyone's hair just right, and convincing your children to cooperate for approximately 30 minutes. 😂",
        "But one of the things that can completely change the feel of your photos is something you might not think about at all: the location.",
        "Your location is more than just a pretty background. It helps set the mood, tells a little bit of your story, and can completely change the way your images feel.",
        "A beautiful location gives us more to work with. It can create gorgeous light, movement, texture, and variety throughout your gallery. A field can give your images a soft, dreamy feel. An open space can create beautiful golden light. A cozy home can make your photographs feel intimate and personal. Even a simple location can become beautiful when we find the right light and the right perspective.",
        "And sometimes, the location doesn't have to be \"picture perfect\" at all.",
        "Some of my favorite photographs come from places that may not look particularly exciting at first glance. What matters is how we use the space.",
        "As a photographer, I am looking at things you probably aren't. Where is the light coming from? What direction should you face? What is happening in the background? Is there somewhere we can walk, sit, play, or simply slow down for a moment?",
        "I am constantly looking for little pockets of beautiful light and opportunities to create variety without making your session feel stiff or overly posed.",
        "That is also why I don't believe every family needs the same type of location.",
        "Your session should feel like you.",
        "Maybe that means running barefoot through a field with your kids. Maybe it means cuddling your newborn in your own home. Maybe it means exploring a beautiful outdoor location together. Maybe it is somewhere that holds special meaning for your family.",
        "The goal isn't simply to find the prettiest place.",
        "It is to find a place that allows your family to connect, move, laugh, and be yourselves.",
        "Because when you look back at your photographs years from now, I want you to remember more than what the background looked like.",
        "I want you to remember how your little girl held your hand.",
        "How your son couldn't stop laughing.",
        "How tiny your baby's fingers were.",
        "How your husband pulled you close.",
        "How it felt to have your whole family together in that exact season of life.",
        "The location is simply the setting.",
        "You are the story.",
        "And that's what makes the photograph beautiful. 🤍",
      ],
      gallery: ["/boudreaux-31.jpg", "/boudreaux-39.jpg", "/family-6.jpg"],
    },
    {
      slug: "the-beauty-of-investing-in-professional-photography",
      title: "The Beauty of Investing in Professional Photography",
      date: "August 2026",
      category: "Personal",
      hero: "/leblanc-50.jpg",
      excerpt:
        "Our phones are pretty incredible — but there is something so special about professional photography that a phone just cannot replace.",
      story: [
        "Let's be honest. Our phones are pretty incredible. They capture the everyday moments, the silly faces, the quick selfies, and approximately 47 pictures of your child doing absolutely nothing. 😂 But there is something so special about professional photography that a phone just cannot replace.",
        "Professional photography is not just about having pretty pictures. It is about capturing the people, personalities, and little moments that make this season of your life so special.",
        "As a photographer, I get to see so many families in front of my camera, and one of my favorite things is watching the little moments unfold. The way your child reaches for your hand. The way your husband looks at you when you are laughing. The little smile that happens when everyone finally relaxes and forgets the camera is even there.",
        "Those are the moments I want to capture.",
        "A professional photographer brings more than a nice camera. There is an understanding of lighting, posing, composition, and how to help you feel comfortable in front of the camera. But more importantly, there is an understanding that the goal is not to make your family look perfect. It is to make your photos feel like you.",
        "And honestly, I think one of the biggest reasons to invest in professional photography is because you deserve to be in the memories too.",
        "How many times have you looked through your camera roll and realized you have hundreds of pictures of your kids, but hardly any with you in them? You are usually the one taking the picture. You are behind the camera, behind the phone, or telling everyone else to smile.",
        "Professional photography gives you the opportunity to put the phone down and actually be part of the memory.",
        "Because your family will not always look like this.",
        "Your babies will grow. Your toddler will eventually stop saying those funny little things you wish you could bottle up forever. Your kids will lose their baby teeth, change their favorite things, grow taller, and become their own little people.",
        "Life changes so quickly.",
        "Photography gives us a way to pause it for just a moment.",
        "And while I love a beautiful digital gallery, there is something about seeing your photographs printed and hanging in your home that feels different. These are the images your children will see years from now. The photographs that may one day be tucked into an album, passed down, or looked at while telling stories about when they were little.",
        "That is why I believe professional photography is an investment.",
        "Not just in beautiful photographs, but in memories, in your family, and in this little chapter of life that you will never get back.",
        "Because one day, these photographs will mean so much more than they do today.",
        "And I think that is pretty beautiful. 🤍",
      ],
      gallery: ["/leblanc-40.jpg", "/leblanc-8.jpg", "/leblanc-6.jpg"],
    },
    {
      slug: "should-you-purchase-the-full-gallery",
      title: "Should You Purchase the Full Gallery? Why I'll Always Say Yes.",
      date: "October 2026",
      category: "Personal",
      hero: "/west-31.webp",
      excerpt:
        "If you've ever finished a session, opened your gallery, and found yourself struggling to choose your favorites — this one is for you.",
      story: [
        "If you've ever finished a photography session, opened your gallery, and found yourself struggling to choose your favorites, this one is for you.",
        "Maybe you've narrowed it down to your package's included images, but there are still so many more you love. Maybe you're wondering if you should spend a little extra to purchase the full gallery. Or maybe you're thinking you can always print the photos later.",
        "As a photographer, my answer will almost always be the same: invest in the images.",
        "And I don't say that simply because photography is my job. I say it because I know just how much these photographs can mean years from now.",
        "## Bring Back the Days of Gathering Around the Family Photos",
        "One of my favorite childhood memories was visiting my Maw Maw's house. She had framed photographs displayed throughout her home, and my cousins and I would each grab the frame of ourselves and look back through the pictures.",
        "We would laugh at our old hairstyles, the outfits we wore, the phases we went through, and all the little things we had completely forgotten. We would sit together, tell stories, and relive those moments as if they had happened yesterday.",
        "There was something so special about that.",
        "We weren't scrolling through a phone or searching through thousands of images on a computer. We were holding those memories in our hands, sharing them with the people who had been there, and creating new memories simply by looking back at the old ones.",
        "That is the kind of experience I want my clients to have with their photographs.",
        "I want your children to walk through your home one day and point to the pictures on your walls. I want your family to pull out an album during the holidays and laugh about how little everyone once was. I want your grandchildren to see what life looked like before they were here.",
        "And I want you to have those photographs to hold onto long after the season of life they captured has passed.",
        "## Your Photos Deserve More Than a Spot on Your Camera Roll",
        "We live in a world where we take hundreds of pictures on our phones, only to let so many of them sit untouched in our camera rolls.",
        "Professional photographs deserve to be enjoyed, displayed, and passed down.",
        "Print your favorites. Put them in frames. Create an album. Fill your home with the faces you love most.",
        "And if you have the option to purchase your full gallery, consider doing it.",
        "The extra images might not seem like a necessity today, but think about what they represent. The in between moments. The unexpected laughter. The little expressions you didn't even notice during your session. The images that show your family exactly as they were in this particular season of life.",
        "Sometimes, the photographs you almost leave behind become the ones you cherish the most.",
        "You may book a session because you want one perfect family portrait for your Christmas card, but years from now, you might find yourself reaching for the photograph of your toddler refusing to cooperate, your baby making the funniest face, or your husband making everyone laugh.",
        "Those imperfect, honest moments are part of your story, too.",
        "## Create Something Your Family Can Pass Down",
        "Imagine your children growing up and having albums filled with photographs from their childhood. Imagine them sitting on the couch with their own children, flipping through the pages and telling stories about the people, places, and moments that shaped their lives.",
        "Imagine them seeing photographs of themselves in your arms, their little hands wrapped around your fingers, or their siblings holding them for the very first time.",
        "These are more than photographs. They are pieces of your family history.",
        "And one day, those albums may mean more to your family than you could ever imagine.",
        "You don't have to print every single image or frame every photograph you receive. Start small. Choose the ones that make you feel something. Create an album each year. Update the frames on your walls as your family grows.",
        "The important thing is to give your memories a place in your home and in your family's story.",
        "## What If You Can't Purchase the Full Gallery Right Now?",
        "I completely understand that photography is an investment, and sometimes the timing just isn't right financially.",
        "That is one of the reasons I keep my client galleries available for three months. You don't have to make every decision the moment your gallery arrives.",
        "If purchasing the full gallery isn't in your budget right now, come back to it when you're ready. And if you need a little flexibility, reach out and ask about a payment plan option.",
        "I would much rather you take the time to figure out what works for your family than feel pressured to make a decision before you're ready.",
        "Your memories are worth preserving, and there is no one right way to do it.",
        "## So, Should You Purchase the Extra Images?",
        "My answer will always be to invest in the images you love.",
        "Invest in the photographs that capture your children at this age, because they won't stay this little forever. Invest in the images that show your family as it is today, in the season you are living right now. Invest in the photographs that capture the raw, real, beautifully imperfect moments that make your story yours.",
        "One day, the little things that feel so ordinary today will be the things you wish you could experience just one more time.",
        "The tiny hands. The missing teeth. The belly laughs. The way your baby fits perfectly in your arms. The way your toddler insists on doing everything themselves. The faces your children make when they think no one is watching.",
        "You may not realize how much you'll miss these moments until they're gone.",
        "Photography gives you a way to hold onto them, even as life continues to change.",
        "So print the photos. Put them in frames. Make the albums. Gather around them with your family, just like we did at my Maw Maw's house.",
        "Let's bring back the days of sitting together, flipping through photographs, laughing at the past, and remembering the little things that made life so special.",
        "Because one day, these won't just be the photographs you purchased. They'll be the memories your family treasures.",
        "And that is an investment you will never regret.",
        "With love,",
        "Jacqueline Claire Photography 🤍",
      ],
      gallery: ["/family-5.jpg", "/aranza-11.webp", "/hanks-46.jpg"],
    },
  ],
};

export const GIFT_CARDS = {
  eyebrow: "The gift of being remembered",
  title: "Session gift cards",
  body: "A photography session is the kind of gift that outlives every trend — for a new mom, a graduating senior, a couple stepping into a new chapter, or the family that's been meaning to book \"one of these years.\" Choose an amount, add a note, and I'll take beautiful care of the rest.",
  ctaLabel: "Purchase a gift card",
  ctaHref: "https://book.usesession.com/i/zvTVBjRIv/gift-cards",
};

export const REVIEW_LINKS = {
  eyebrow: "Share your story",
  title: "Have your own story to add?",
  body: "Your words mean everything — they help other families find their photographer. If you'd like to share your experience, it would mean the world to me and this small business.",
  google: "https://www.google.com/maps/place/Jacqueline+Claire+Photography/@30.2533113,-92.4203082,10z/data=!4m8!3m7!1s0x2ec77c59ea44d97d:0x60923de2bf724ff5!8m2!3d30.253724!4d-92.090669!9m1!1b1!16s%2Fg%2F11y49w3m1y?entry=ttu",
  facebook: "https://www.facebook.com/jacclairephotos/reviews/",
  editingNote:
    "Once you've had a chance to soak in your gallery, sharing a review helps this small business more than you know.",
};

// MINI_SESSIONS — add or remove seasonal mini-session dates here.
// Leave the array empty to hide the popup entirely.
// Each item: { theme, date, href?, status? }
//   • Add `href` to make the row a live booking link.
//   • Omit `href` and it defaults to "Coming soon". Override with `status: "Stay tuned"` etc.
export const MINI_SESSIONS = {
  eyebrow: "Seasonal mini sessions",
  title: "A few dates on the horizon",
  bookLabel: "Book",
  sessions: [
    { theme: "Holiday Portraits With Santa", date: "October 31", href: "https://book.usesession.com/s/GUMRCQy548" },
    { theme: "Tree Farm Minis", date: "November 7", href: "https://book.usesession.com/s/GnZoclE3qS" },
    { theme: "Family Christmas Minis", date: "November 14", href: "https://book.usesession.com/s/RD3jKgKjHl" },
    { theme: "Holiday Portraits With Santa", date: "November 22", href: "https://book.usesession.com/s/GUMRCQy548" },
    { theme: "TBD — possibly another Christmas Studio Mini", date: "December 5", status: "Stay tuned" },
  ],
};

export const SESSION_TYPES = [
  "Studio Portrait",
  "Family",
  "Senior",
  "Branding / Headshots",
  "Maternity",
  "Couples",
  "Lifestyle Newborn",
  "Boudoir",
  "Not sure yet",
];
